import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { notificationSSEService } from '../services/notificationSSEService';
import { Window } from '../utils/window';
import { t } from '../i18n/i18n';

const NotificationContext = createContext();

const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter((n) => n.id !== action.payload),
      };
    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: [],
      };
    case 'SET_CONNECTED':
      return {
        ...state,
        isConnected: action.payload,
      };
    default:
      return state;
  }
};

const initialState = {
  notifications: [],
  isConnected: false,
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const { authenticated, user, currentWorkspace } = useSelector((state) => state.auth);
  const token = Window() ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (Window() && authenticated && token && currentWorkspace && user?.id) {
      const connectSSE = async () => {
        try {
          await notificationSSEService.connect(token, currentWorkspace);
          dispatch({ type: 'SET_CONNECTED', payload: true });
        } catch (error) {
          console.error('Failed to connect to SSE:', error);
          dispatch({ type: 'SET_CONNECTED', payload: false });
        }
      };

      connectSSE();

      const unsubscribe = notificationSSEService.addListener((notification) => {
        // Use requestAnimationFrame to ensure the state update happens outside the current render cycle
        requestAnimationFrame(() => {
          try {
            // Validate and sanitize the notification data
            if (!notification || typeof notification !== 'object') {
              console.warn('Invalid notification received:', notification);
              return;
            }

            // Utility function to replace placeholders in text with payload values
            const replacePlaceholders = (text, payload) => {
              if (!text || !payload || typeof text !== 'string') return text;

              // Replace all {key} placeholders with corresponding payload values
              return text.replace(/{([^}]+)}/g, (match, key) => {
                // Return the payload value if it exists, otherwise return the original placeholder
                return payload[key] !== undefined ? payload[key] : match;
              });
            };

            // Helper function to detect and translate whitelabel tags with payload replacement
            const translateIfNeeded = (text, payload = null) => {
              if (!text) return '';

              // Check if text looks like a translation key (uppercase with underscores)
              const isTranslationKey = /^[A-Z][A-Z0-9_]*[A-Z0-9]$/.test(text);

              let processedText = text;

              if (isTranslationKey) {
                // Try to translate, fallback to original text if translation not found
                const translated = t(text);
                // If translation returns the same key, it means translation wasn't found
                processedText = translated === text ? text : translated;
              }

              // Apply payload replacements if payload is provided
              if (payload) {
                processedText = replacePlaceholders(processedText, payload);
              }

              return processedText;
            };

            // Smart type detection based on notification content (fallback if type not provided)
            const detectNotificationType = (title, description, providedType) => {
              if (providedType && ['success', 'info', 'warning', 'error'].includes(providedType)) {
                return providedType;
              }

              const text = `${title} ${description}`.toLowerCase();
              if (text.includes('error') || text.includes('failed') || text.includes('problem')) {
                return 'error';
              }
              if (
                text.includes('success') ||
                text.includes('completed') ||
                text.includes('approved')
              ) {
                return 'success';
              }
              if (
                text.includes('warning') ||
                text.includes('attention') ||
                text.includes('review')
              ) {
                return 'warning';
              }
              return 'info';
            };

            // Map action properties from backend format to frontend format
            const mapActions = (actions, payload) => {
              if (!Array.isArray(actions)) return [];
              return actions.map((action) => ({
                label: translateIfNeeded(action.label, payload),
                actionUrl: replacePlaceholders(action.action || action.actionUrl, payload), // Support both 'action' and 'actionUrl'
                type:
                  action.style === 'secondary'
                    ? 'default'
                    : action.style || action.type || 'default', // Map 'style' to 'type'
              }));
            };

            // Extract payload from notification
            const payload = notification.payload || {};

            // Process title and description with translation and placeholders
            const processedTitle = translateIfNeeded(notification.title || '', payload);
            const processedDescription = translateIfNeeded(notification.description || '', payload);
            const processedSender = translateIfNeeded(notification.sender || '', payload);

            // Map backend notification structure to frontend structure
            const newNotification = {
              // Core properties
              id: notification.id || `${Date.now()}-${Math.random()}`,
              timestamp: notification.createdAt ? new Date(notification.createdAt) : new Date(),

              // Content - with translation and placeholder replacement
              title: processedTitle,
              message: processedTitle,
              description: processedDescription,
              body: processedDescription,

              // Ant Design Alert properties
              type: detectNotificationType(processedTitle, processedDescription, notification.type),
              showIcon: notification.showIcon !== false, // Default true unless explicitly false
              closable: notification.closable !== false, // Default true unless explicitly false
              banner: notification.banner || false,

              // Auto-dismiss behavior
              autoClose: notification.autoClose !== false, // Default true unless explicitly false
              duration: notification.duration || 5000,

              // Visual properties
              className: notification.className || '',
              style: notification.style || {},
              priority: notification.priority || 'normal',

              // Interaction
              onClick: notification.onClick || !!notification.actionUrl,
              actionUrl: replacePlaceholders(notification.actionUrl || null, payload),
              actions: mapActions(notification.actions, payload),

              // Metadata - with translation and placeholder replacement
              isRead: notification.isRead || false,
              sender: processedSender,
              category: t(notification.category || ''),
              tags: notification.tags || [],
              messageType: notification.messageType || 'text',

              // Keep original payload for reference
              payload: payload,
            };

            dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
          } catch (error) {
            console.error('Error processing notification:', error, notification);
          }
        });
      });

      return () => {
        unsubscribe();
        notificationSSEService.disconnect();
        dispatch({ type: 'SET_CONNECTED', payload: false });
      };
    }

    return () => {
      if (Window()) {
        notificationSSEService.disconnect();
        dispatch({ type: 'SET_CONNECTED', payload: false });
      }
    };
  }, [authenticated, token, currentWorkspace, user?.id]);

  const removeNotification = useCallback((notificationId) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
  }, []);

  const clearAllNotifications = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const value = useMemo(
    () => ({
      notifications: state.notifications,
      isConnected: state.isConnected,
      removeNotification,
      clearAllNotifications,
    }),
    [state.notifications, state.isConnected, removeNotification, clearAllNotifications]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
